import { useToast } from '@chakra-ui/react'
import { TableBuildProps } from 'components/organisms/TableBuild/interfaces'
import { TableBuildActionsType } from 'components/organisms/TableBuild/type'
import { useAuthContext } from 'contexts/auth'
import { useContractContext } from 'contexts/contract'
import { useInstitutionsContext } from 'contexts/institutions'
import {
    ContractsOrderBy,
    ContractsQueryDTO,
    ContractsResponseDTO
} from 'models'
import { FilterContracts } from 'pages/Contracts/Components/Filter/interfaces'
import { StatusBadge } from 'pages/Contracts/Components/StatusBadge'
import { defaultHeaders } from 'pages/Contracts/constants'
import { TableContracts } from 'pages/Contracts/types'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import formatter from 'utils/formatter'
import { useCacheFilters } from './useCacheFilters'

interface RequestContractsDTO {
    query?: ContractsQueryDTO
    page?: number
    limit?: number
}

export const useContracts = () => {
    const [orderByContracts, setOrderByContracts] = useState<
        Required<Pick<ContractsQueryDTO, 'ordenacao' | 'tipoOrdenacao'>>
    >({
        ordenacao: 'dataRegistro',
        tipoOrdenacao: 'DESC'
    })

    const [tableContracts, setTableContracts] = useState<
        TableBuildProps<TableContracts>
    >({
        header: defaultHeaders as any,
        action: [
            {
                type: 'PREVIEW'
            }
        ]
    })

    const { currentInstitution, institutions } = useInstitutionsContext()
    const { isBackoffice } = useAuthContext()

    const toast = useToast()

    const [pageContracts, setPageContracts] = useState<number>(1)
    const [limitContracts, setLimitContracts] = useState(10)
    const [totalPagesContracts, setTotalPagesContracts] = useState(1)
    const [filtersContracts, setFiltersContracts] = useState<FilterContracts>(
        {} as FilterContracts
    )

    const [loadingTableContracts, setLoadingTableContracts] = useState(true)

    const { persistCacheFilters, getCacheFilters } = useCacheFilters()

    const navigate = useNavigate()

    const { getContracts } = useContractContext()

    const setCacheFilters = () => {
        const cacheData = {
            currentInstitution,
            institutions,
            page: pageContracts,
            limit: limitContracts,
            from: 'contratos',
            to: 'contratos',
            filter: filtersContracts
        }

        persistCacheFilters(cacheData)
    }

    const handleActionContracts = (
        _: TableBuildActionsType,
        index: number | string
    ) => {
        setCacheFilters()
        navigate(`/contratos/${index}`)
    }

    const handleLimitContracts = (limit: number) => {
        const queryParams = {
            query: filtersContracts,
            page: 1,
            limit
        }

        setLoadingTableContracts(true)
        setLimitContracts(limit)
        setPageContracts(1)
        fetchContracts(queryParams)
    }

    const handlePageContracts = (page: number) => {
        if (pageContracts !== page) {
            const queryParams = {
                query: filtersContracts,
                page,
                limit: limitContracts
            }

            setLoadingTableContracts(true)
            setPageContracts(page)
            fetchContracts(queryParams)
        }
    }

    const buildTable = (contracts: ContractsResponseDTO[]) => {
        return contracts.map(
            ({
                status,
                valorTotalDivida,
                detranRegistro,
                dataRegistro,
                placa,
                ...contract
            }) => ({
                ...contract,
                status: <StatusBadge status={status} />,
                placa: formatter.placa(placa),
                dataRegistro: formatter.data(dataRegistro),
                detranRegistro: formatter.data(detranRegistro),
                valorTotalDivida: formatter.moeda(valorTotalDivida)
            })
        )
    }

    const handleFilterContracts = (filters: FilterContracts) => {
        setLoadingTableContracts(true)
        setFiltersContracts(filters)
        setPageContracts(1)
        filters.dataInicio = filters.dataInicio
            ? formatter.timeConverter(filters.dataInicio)
            : ''
        filters.dataFim = filters.dataFim ?? ''
        filters.placa = filters.placa ? filters.placa.replace('-', '') : ''
        fetchContracts({
            page: 1,
            query: { ...filters },
            limit: limitContracts
        })
    }

    const handleOrderByContracts = (ordenacao: ContractsOrderBy) => {
        setLoadingTableContracts(true)
        setOrderByContracts(old => ({
            ordenacao,
            tipoOrdenacao:
                old.ordenacao !== ordenacao
                    ? old.tipoOrdenacao
                    : old.tipoOrdenacao === 'ASC'
                    ? 'DESC'
                    : 'ASC'
        }))
    }

    const fetchContracts = async ({
        query,
        page,
        limit
    }: RequestContractsDTO) => {
        try {
            if (currentInstitution?.id === undefined) {
                setLoadingTableContracts(true)
                return
            }

            setLoadingTableContracts(true)

            const { data, headers } = await getContracts(
                {
                    ...(query ?? {}),
                    ...orderByContracts,
                    pagina: page ?? pageContracts,
                    limite: limit ?? limitContracts
                },
                isBackoffice,
                currentInstitution?.id as string
            )

            setTableContracts(old => ({
                ...old,
                body: buildTable(data)
            }))
            setTotalPagesContracts(+headers['total-pages'])
        } catch {
            toast({
                title: 'Sentimos muito!',
                description: 'Ocorreu algum erro, tente novamente mais tarde.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
        } finally {
            setLoadingTableContracts(currentInstitution?.id === undefined)
        }
    }

    const fetchContractsWithCache = () => {
        const cacheFilters = getCacheFilters()!

        if (!cacheFilters) return

        const { page, limit, filter } = cacheFilters

        setPageContracts(page)
        setLimitContracts(limit)
        setFiltersContracts(filter)
        fetchContracts({
            query: { ...(filter ?? {}) },
            page: page ?? 1,
            limit: limit ?? 10
        })
    }

    return {
        pageContracts,
        limitContracts,
        totalPagesContracts,
        fetchContracts,
        handleActionContracts,
        handleLimitContracts,
        handlePageContracts,
        loadingTableContracts,
        fetchContractsWithCache,
        handleFilterContracts,
        tableContracts,
        handleOrderByContracts,
        orderByContracts,
        filtersContracts
    }
}
